/**
 * playerPreviewModel — pure GraphQL → view-model mapping for the player
 * preview drawer (SE-5494).
 *
 * The drawer renders eight distinct sections off one `PlayerDetailGetPlayer`
 * result. Doing that mapping inside the View would bury a pile of product rules
 * (which fields are deprecated, which carry sentinels, what "market value"
 * means) in JSX where nothing can test them. So the whole projection lives here
 * as pure functions and the View just renders rows.
 *
 * Deprecation traps encoded here on purpose — every one of these is a field the
 * obvious implementation would have reached for and got `null` from:
 * - GPR reads `bioData.gpr`, NEVER `Player.gpr` (deprecated; null outside
 *   `searchPlayers`).
 * - Market value reads the `min/maxGeminiPlayerValuation` PAIR, never the
 *   deprecated `expectedFee` / `fairFee`.
 * - Club reads `bioData.currentClub` / `team.name`, never the deprecated
 *   `Player.clubName`.
 * - `Player.gpm`, `timeDecayedGPR`, `vaep`, `zGpm`, `zVaep` are documented
 *   "always null — nothing populates this field" and are not read at all.
 * - Agent/agency goes through {@link resolvePlayerAgent} so the backend's
 *   literal `"no agent"` sentinel never reaches the UI (SE-5494 AC5).
 * @module features/player-kanban/utils/playerPreviewModel
 */

import { DEFAULT_UI_LOCALE } from "@/constants/locales";
import { formatGeminiValuationRange } from "@/utils/formatCurrencyShort";
import { formatNumber } from "@/utils/intlFormat";
import { CurrencyCode } from "@/generated/graphql";
import { resolvePlayerAgent } from "./playerAgent";

/** Rendered when a field has no value. A row is always shown, never dropped. */
export const EMPTY_VALUE = "—";

/** The three states the "Watched" row can report. Absent is not "not watched". */
type WatchedKey = "watched" | "notWatched" | "unknown";

/** Pure data — the boolean is converted to a key before lookup. */
const WATCHED_LABELS: Record<WatchedKey, string> = {
  watched: "Yes",
  notWatched: "No",
  unknown: EMPTY_VALUE,
};

/**
 * Narrow the tri-state watched flag to a lookup key.
 * @param isWatched - The raw flag; nullish means the field was absent.
 * @returns The lookup key for {@link WATCHED_LABELS}.
 */
const toWatchedKey = (isWatched: boolean | null | undefined): WatchedKey => {
  if (isWatched == null) return "unknown";
  return isWatched ? "watched" : "notWatched";
};

/** One label/value row in a drawer section. */
export interface PlayerPreviewInfoItem {
  /** Stable key — also the row's testID suffix. Never localised. */
  readonly key: string;
  /** Human-readable row label. */
  readonly label: string;
  /** Formatted value, or {@link EMPTY_VALUE} when absent. */
  readonly value: string;
  /**
   * 0-100 bar fill for rows the drawer draws as a meter, or `undefined` for
   * rows that carry no meter.
   *
   * `undefined` is the DEFAULT, not the exception. A meter asserts "this far
   * along a known scale", so a row earns one only where the scale is actually
   * known and documented: GPR and `teamStyleFit` (0-100), and `fitScore` once
   * scaled off its 0-1 decimal. Text rows, absent values, and every score whose
   * range nobody has written down — the categorical attributes and Physical —
   * carry no bar. See {@link buildAttributesRows}.
   *
   * The percentage is computed here rather than in the View so the decision of
   * WHICH scale a number lives on is testable, instead of being buried in JSX
   * where a wrong denominator looks exactly like a right one.
   */
  readonly percent?: number;
  /**
   * Which fill a meter row draws. `success` is reserved for GPR — the design
   * gives the headline rating its own green so it reads as the summary number
   * the rest of the section decomposes; every other meter takes the neutral
   * foreground fill.
   */
  readonly barTone?: "foreground" | "success";
}

/**
 * Upper bound of the 0-100 rating scale (GPR, team style fit).
 * @remarks
 * Deliberately NOT applied to the categorical attributes any more — see
 * {@link buildAttributesRows} for why those carry no bar at all.
 */
const RATING_SCORE_MAX = 100;

/**
 * Multiplier turning the backend's 0-1 `fitScore` into a percentage.
 * @remarks
 * `bioData.fitScore` arrives as a DECIMAL and three independent sites already
 * say so: `getPlayerCompositeMetrics` renders it as `fitScore * 100`,
 * `playerPageHeaderUtils` carries the comment "fitScore is 0-1 from backend,
 * multiply by 100 for display", and `filterDefaults` keeps a separate
 * `DEFAULT_SCORE_RANGE_DECIMAL: [0, 1]` naming fitScore as its member.
 *
 * The drawer was the one surface treating it as 0-100, so a 0.8 fit rendered as
 * the string "0.8" beside a bar 0.8% wide — a full-width empty track. Caught
 * while fixing the attribute bars in the same section.
 */
const FIT_SCORE_PERCENT_MULTIPLIER = 100;

/**
 * Normalise `fitScore` to a percentage, tolerating an already-scaled value.
 * @remarks
 * The guard is the shipped `formatPercentage` idiom (`utils/playerFormatting`):
 * scale a decimal, pass anything above 1 through untouched. Without it, a
 * backend that ever starts returning 0-100 would silently render 8000%.
 * @param value - The raw fit score.
 * @returns The 0-100 value, or `undefined` when absent.
 */
const toFitPercentValue = (
  value: number | null | undefined
): number | undefined => {
  if (value == null || Number.isNaN(value)) return undefined;
  return value > 1 ? value : value * FIT_SCORE_PERCENT_MULTIPLIER;
};

/**
 * Convert a raw score to a 0-100 bar fill against its own scale.
 * @remarks
 * Clamped, so an out-of-band value from the provider draws a full or empty
 * bar rather than overflowing its track.
 * @param value - The raw score.
 * @param max - The upper bound of the scale this score lives on.
 * @returns The clamped percentage, or `undefined` when the score is absent.
 */
const toPercent = (
  value: number | null | undefined,
  max: number
): number | undefined => {
  if (value == null || Number.isNaN(value)) return undefined;
  return Math.max(0, Math.min(100, (value / max) * 100));
};

/** One row in the drawer's scout-report list. */
export interface PlayerPreviewReportItem {
  /** Report id (falls back to a positional key when the API omits it). */
  readonly key: string;
  /** Scout who filed the report. */
  readonly scoutName: string;
  /** Formatted report date. */
  readonly reportDate: string;
  /** The scout's overall score, formatted; `undefined` when unscored. */
  readonly overallScore?: string;
}

/**
 * The minimum player shape the projection needs.
 * @remarks
 * Structural rather than the generated `PlayerDetailPlayerMetaFragment` so
 * tests and partial cache reads type-check against the seam instead of casting
 * a half-built fixture through `as`.
 */
export interface PreviewablePlayer {
  readonly id?: string | null;
  readonly name?: string | null;
  readonly knownName?: string | null;
  readonly teamName?: string | null;
  readonly team?: {
    readonly name?: string | null;
    readonly league?: { readonly name?: string | null } | null;
  } | null;
  readonly isWatchedByCurrentUser?: boolean | null;
  readonly nationality?: { readonly name?: string | null } | null;
  /**
   * Server-side totals for the player's transferable data. The drawer shows a
   * capped PAGE of notes and reports, so these — not the fetched array lengths
   * — are what the "View all notes (N)" and "Reports N" counters must read.
   * Documented null for provider-sourced players.
   */
  readonly transferableDataCounts?: {
    readonly scoutReportCount?: number | null;
    readonly noteCount?: number | null;
  } | null;
  readonly positions?:
    | readonly ({
        readonly abbreviation?: string | null;
        readonly name?: string | null;
      } | null)[]
    | null;
  readonly bioData?: {
    readonly age?: number | null;
    readonly gpr?: number | null;
    readonly foot?: string | null;
    readonly height?: number | null;
    readonly primaryPosition?: string | null;
    readonly isInjured?: boolean | null;
    readonly contractExpires?: string | null;
    readonly currentClub?: string | null;
    readonly currentLeague?: string | null;
    readonly minGeminiPlayerValuation?: number | null;
    readonly maxGeminiPlayerValuation?: number | null;
    readonly playerAgent?: string | null;
    readonly roleArchetype?: string | null;
    readonly secondaryRoleArchetype?: string | null;
    readonly fitScore?: number | null;
    readonly teamStyleFit?: number | null;
    readonly physicalScore?: number | null;
    readonly carryingTransGlobalCategoricalScore?: number | null;
    readonly dribblingTransGlobalCategoricalScore?: number | null;
    readonly passingTransGlobalCategoricalScore?: number | null;
    readonly shotLocationTransGlobalCategoricalScore?: number | null;
  } | null;
  readonly scoutReports?:
    | readonly ({
        readonly reportId?: string | null;
        readonly scoutName?: string | null;
        readonly reportDate?: string | null;
        readonly overallScore?: number | null;
        readonly ggMatchKeys?: readonly string[] | null;
        readonly matches?:
          | readonly ({
              readonly matchDate?: string | null;
              readonly opponent?: string | null;
            } | null)[]
          | null;
      } | null)[]
    | null;
}

/**
 * Coalesce a possibly-absent value into a displayable string.
 * @param value - Candidate value.
 * @returns The trimmed string, or {@link EMPTY_VALUE}.
 */
const orEmpty = (value: string | null | undefined): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : EMPTY_VALUE;
};

/**
 * Format a numeric score to at most one decimal place.
 * @param value - The score.
 * @returns The formatted score, or `undefined` when absent.
 */
const formatScore = (value: number | null | undefined): string | undefined =>
  value == null || Number.isNaN(value)
    ? undefined
    : String(Math.round(value * 10) / 10);

/**
 * Format an ISO date as a short, locale-independent `DD MMM YYYY`.
 * @remarks
 * Deliberately not `toLocaleDateString` — a locale-dependent string would make
 * the e2e/unit assertions machine-dependent.
 * @param iso - ISO timestamp.
 * @returns The formatted date, or `undefined` when absent/unparseable.
 */
export const formatPreviewDate = (
  iso: string | null | undefined
): string | undefined => {
  if (!iso) return undefined;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return undefined;
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const day = String(parsed.getUTCDate()).padStart(2, "0");
  return `${day} ${months[parsed.getUTCMonth()]} ${parsed.getUTCFullYear()}`;
};

/**
 * Format the preferred-foot value for display.
 * @remarks
 * Mirrors the player-profile header's handling: `"both"` becomes `"Both"`,
 * anything else is capitalised.
 * @param foot - Raw `bioData.foot`.
 * @returns The formatted foot, or `undefined` when absent.
 */
export const formatFoot = (
  foot: string | null | undefined
): string | undefined => {
  const trimmed = foot?.trim();
  if (!trimmed) return undefined;
  if (trimmed.toLowerCase() === "both") return "Both";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

/**
 * Format a height in centimetres.
 * @param height - Raw `bioData.height` (centimetres).
 * @returns e.g. `"183 cm"`, or `undefined` when absent.
 */
export const formatHeight = (
  height: number | null | undefined
): string | undefined =>
  height == null || Number.isNaN(height) || height <= 0
    ? undefined
    : `${Math.round(height)} cm`;

/**
 * The player's display name, preferring the known/common name.
 * @param player - The player.
 * @returns The display name, or {@link EMPTY_VALUE}.
 */
export const selectDisplayName = (player: PreviewablePlayer): string =>
  orEmpty(player.knownName ?? player.name);

/**
 * The player's current club/team name.
 * @remarks
 * Reads `team.name` → `teamName` → `bioData.currentClub`. Never the deprecated
 * `Player.clubName`.
 * @param player - The player.
 * @returns The club name, or {@link EMPTY_VALUE}.
 */
export const selectClubName = (player: PreviewablePlayer): string =>
  orEmpty(
    player.team?.name ?? player.teamName ?? player.bioData?.currentClub ?? null
  );

/**
 * The player's position label.
 * @remarks
 * Prefers the explicit `bioData.primaryPosition`, falling back to the joined
 * `positions[]` abbreviations.
 *
 * **Why an unmatched `primaryPosition` falls through (SE-8168).** This drawer
 * shares the same `bioData.primaryPosition` disagreement risk as the Pipeline
 * card chip fixed by {@link getPlayerPosition} in `manualPlayerUtils.ts` —
 * `primaryPosition` is free text and can name a side/position the player's
 * own `positions[]` doesn't corroborate (confirmed live for Enzo Fernández:
 * `primaryPosition` read `"Left Defensive Midfielder"` while `positions` held
 * only `"Right Defensive Midfielder"`). The card fix landed first and this
 * drawer was missed — same bug, sibling surface. So `primary` is only
 * trusted when some `positions[]` entry actually names it; otherwise it is
 * discarded (not rendered) in favor of the joined-abbreviations fallback,
 * matching what already happens when `primaryPosition` is absent.
 * @param player - The player.
 * @returns The position label, or {@link EMPTY_VALUE}.
 */
export const selectPositionLabel = (player: PreviewablePlayer): string => {
  const primary = player.bioData?.primaryPosition?.trim();
  if (primary) {
    const named = player.positions?.find(
      position => position?.name?.trim().toLowerCase() === primary.toLowerCase()
    );
    // Only trust `primary` as display text when some position entry actually
    // corroborates it — see the SE-8168 remark above. Prefer the matched
    // entry's own `name` (canonical casing) over the raw `primary` string.
    if (named) return named.name?.trim() || primary;
  }

  const abbreviations = (player.positions ?? [])
    .map(position => position?.abbreviation ?? position?.name ?? "")
    .map(value => value.trim())
    .filter(Boolean);

  return abbreviations.length ? abbreviations.join(", ") : EMPTY_VALUE;
};

/**
 * How recent a pinned note must be to carry the "NEW" badge, in whole days.
 * Mirrors the PRD's pinning rule — the note itself pins with NO recency
 * window (AC1/AC2), and only the BADGE is time-boxed.
 */
const PINNED_NOTE_NEW_WINDOW_DAYS = 7;

/** Milliseconds in a day, for whole-day note ages. */
const MS_PER_DAY = 86_400_000;

/**
 * Whole days between a timestamp and now, floored.
 * @param iso - ISO timestamp.
 * @param now - The reference instant (injectable so tests are not clock-bound).
 * @returns The age in whole days, or `undefined` when unparseable.
 */
const ageInDays = (
  iso: string | null | undefined,
  now: Date
): number | undefined => {
  if (!iso) return undefined;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return Math.floor((now.getTime() - parsed.getTime()) / MS_PER_DAY);
};

/**
 * Whether the pinned note is recent enough to badge as NEW.
 * @remarks
 * A FUTURE timestamp (clock skew between server and device) is not treated as
 * new — a negative age is not evidence of recency, it is evidence of a bad
 * clock.
 * @param iso - The note's creation timestamp.
 * @param now - The reference instant.
 * @returns Whether the note falls inside the NEW window.
 */
export const isPinnedNoteNew = (
  iso: string | null | undefined,
  now: Date = new Date()
): boolean => {
  const days = ageInDays(iso, now);
  if (days === undefined) return false;
  return days >= 0 && days < PINNED_NOTE_NEW_WINDOW_DAYS;
};

/**
 * The compact age the NEW badge prints beside itself — `"2d"`, `"0d"` today.
 * @param iso - The note's creation timestamp.
 * @param now - The reference instant.
 * @returns The compact age, or `undefined` when unparseable.
 */
export const formatPinnedNoteAge = (
  iso: string | null | undefined,
  now: Date = new Date()
): string | undefined => {
  const days = ageInDays(iso, now);
  if (days === undefined || days < 0) return undefined;
  return `${days}d`;
};

/** What the hero's status dot may say. Absent means "the data does not say". */
export type PlayerAvailability = "available" | "injured";

/**
 * The player's availability, for the hero avatar's status dot (SE-6554).
 * @remarks
 * `bioData.isInjured` is a genuine TRI-state and is read as one: `true` is
 * injured, `false` is available, and NULLISH means the provider told us
 * nothing. The third case returns `undefined` so the drawer can omit the dot
 * entirely — a green "available" disc rendered off a null would be the UI
 * asserting a clean bill of health nobody actually reported.
 * @param player - The player.
 * @returns The availability, or `undefined` when unknown.
 */
export const selectAvailability = (
  player: PreviewablePlayer
): PlayerAvailability | undefined => {
  const isInjured = player.bioData?.isInjured;
  if (isInjured == null) return undefined;
  return isInjured ? "injured" : "available";
};

/**
 * Total notes on the player, for the "View all notes (N)" counter.
 * @param player - The player.
 * @returns The server-side note count, or `undefined` when unreported.
 */
export const selectNoteCount = (
  player: PreviewablePlayer
): number | undefined => player.transferableDataCounts?.noteCount ?? undefined;

/**
 * Total scout reports on the player, for the "Reports N" counter.
 * @remarks
 * Read from the server's count rather than `reports.length`, which is only as
 * long as the page `PlayerDetailGetPlayer` returned.
 * @param player - The player.
 * @returns The server-side report count, or `undefined` when unreported.
 */
export const selectReportCount = (
  player: PreviewablePlayer
): number | undefined =>
  player.transferableDataCounts?.scoutReportCount ?? undefined;

/**
 * The hero's one-line identity summary — "Millonarios · 24y · Portugal".
 * @remarks
 * POSITION is deliberately absent: the design leads the line with it as a
 * filled chip rather than a joined segment, so the View renders
 * {@link selectPositionLabel} separately and this string is what follows it.
 *
 * Composed here, not in the View, so the separator and the dropped-segment
 * rule live with the rest of the projection. Segments that resolve to
 * {@link EMPTY_VALUE} are OMITTED rather than printed: on a joined meta line an
 * em dash reads as a typo, whereas on a labelled key-info row it reads as
 * "we have no value" — which is why the same absent field is dropped here and
 * kept there.
 *
 * Nationality prints in full. The design abbreviates it to a two-letter
 * country code, but `Nationality` exposes only `id` and `name`, and deriving a
 * code from a display name is the kind of guess that mislabels a player's
 * country.
 * @param player - The player.
 * @returns The joined meta line, or an empty string when nothing is known.
 */
export const selectHeroMeta = (player: PreviewablePlayer): string =>
  [
    selectClubName(player),
    player.bioData?.age == null ? EMPTY_VALUE : `${player.bioData.age}y`,
    orEmpty(player.nationality?.name),
  ]
    .filter(segment => segment !== EMPTY_VALUE)
    .join(" · ");

/**
 * Build the Key info section (SE-5494).
 * @remarks
 * EVERY row ALWAYS renders — an absent value shows {@link EMPTY_VALUE} rather
 * than collapsing the row. The demo regression this closes was "agent/agency is
 * missing", which a drop-empty-rows policy would silently reproduce the moment
 * the field is null.
 *
 * `age`, `league` and `watched` are carried over from the panel this drawer
 * replaced: SE-5494 enumerates what the drawer must SHOW, it never authorised
 * removing anything from this surface, and "it is still on the full profile" is
 * exactly the reasoning that ships silent regressions.
 * @param player - The player.
 * @param currencyCode - The viewing organisation's currency.
 * @returns The Key info rows, in render order.
 */
export const buildKeyInfoRows = (
  player: PreviewablePlayer,
  currencyCode: CurrencyCode = CurrencyCode.Eur
): readonly PlayerPreviewInfoItem[] => {
  const bio = player.bioData;

  return [
    { key: "position", label: "Position", value: selectPositionLabel(player) },
    {
      key: "foot",
      label: "Preferred foot",
      value: orEmpty(formatFoot(bio?.foot)),
    },
    {
      key: "height",
      label: "Height",
      value: orEmpty(formatHeight(bio?.height)),
    },
    {
      key: "age",
      label: "Age",
      value: bio?.age == null ? EMPTY_VALUE : `${bio.age}`,
    },
    {
      key: "nationality",
      label: "Nationality",
      value: orEmpty(player.nationality?.name),
    },
    {
      key: "league",
      label: "League",
      value: orEmpty(player.team?.league?.name ?? bio?.currentLeague),
    },
    {
      key: "market-value",
      label: "Market value",
      // The min/max PAIR — `expectedFee`/`fairFee` are deprecated.
      value: orEmpty(
        formatGeminiValuationRange(
          bio?.minGeminiPlayerValuation,
          bio?.maxGeminiPlayerValuation,
          currencyCode
        )
      ),
    },
    {
      key: "contract-expires",
      label: "Contract until",
      value: orEmpty(formatPreviewDate(bio?.contractExpires)),
    },
    {
      key: "agent",
      label: "Agent / agency",
      // SE-5494 AC5 — the `"no agent"` sentinel is filtered here.
      value: orEmpty(resolvePlayerAgent(bio?.playerAgent)),
    },
    {
      key: "gpr",
      label: "GPR",
      // `bioData.gpr` — `Player.gpr` is deprecated and null off searchPlayers.
      value: orEmpty(formatScore(bio?.gpr)),
    },
    {
      key: "watched",
      label: "Watched",
      // Boolean, so it renders Yes/No rather than an em dash — but a NULLISH
      // value (the field genuinely absent) still reads as unknown.
      value: WATCHED_LABELS[toWatchedKey(player.isWatchedByCurrentUser)],
    },
  ];
};

/**
 * Build the Attributes section — the per-skill categorical scores.
 * @remarks
 * Carried over from the panel this drawer replaced, which rendered exactly
 * these four. Sources match `usePlayerDataTransform` (note "Shooting" maps to
 * `shotLocationTransGlobalCategoricalScore`, not a `shooting*` field — there
 * isn't one).
 *
 * ## These rows carry NO BAR, and that is the fix (2026-08-07)
 *
 * They used to divide by a `CATEGORICAL_SCORE_MAX` of 10 on the stated premise
 * that categorical scores are 0-10. **The premise was false.** Raniel's drawer
 * showed Carrying 109.3, Dribbling 129, Passing 84.6, Shooting 103, so
 * `toPercent` clamped ALL FOUR to a full bar — the section was four identical
 * full tracks, and "o progress bar não corresponde aos valores".
 *
 * Raising the denominator instead would only move the lie, because **no
 * authoritative bound for these fields exists anywhere**: the schema documents
 * none (`Float`, nullable, doc-string "Carrying global categorical score
 * (transformed)"), org config has none, and the only per-metric numbers in the
 * codebase — 40/85 in `getPlayerCompositeMetrics` — are a COLOUR-saturation
 * window fed to `getColorClass`, never a display domain. Values run roughly
 * 40-130. A progress bar asserts "this far along a known scale"; with no known
 * scale, every denominator is a guess dressed as a measurement.
 *
 * So the drawer now does what the PROFILE does with these same four fields.
 * `PlayerDetailCenter` — the profile page body — renders them through
 * `getPlayerCompositeMetrics` into `CompositeMetric` cards: value and label,
 * colour-tinted, **no track and no percentage**. The panel this drawer replaced
 * did the same (`PlayerAttributes` → `StatDisplayRow`). Following that
 * precedent is the same verbatim-reuse rule Raniel applied to the GPR badge,
 * and it makes the two surfaces agree by construction.
 *
 * Omitting `percent` is already a supported state, not a special case: the View
 * renders no track when it is undefined, which the "absent score gets NO bar"
 * test has pinned since SE-6554.
 * @param player - The player.
 * @returns The attribute rows, in render order, carrying values but no meters.
 */
export const buildAttributesRows = (
  player: PreviewablePlayer
): readonly PlayerPreviewInfoItem[] => {
  const bio = player.bioData;

  return [
    {
      key: "carrying",
      label: "Carrying",
      value: orEmpty(formatScore(bio?.carryingTransGlobalCategoricalScore)),
    },
    {
      key: "dribbling",
      label: "Dribbling",
      value: orEmpty(formatScore(bio?.dribblingTransGlobalCategoricalScore)),
    },
    {
      key: "passing",
      label: "Passing",
      value: orEmpty(formatScore(bio?.passingTransGlobalCategoricalScore)),
    },
    {
      key: "shooting",
      label: "Shooting",
      value: orEmpty(formatScore(bio?.shotLocationTransGlobalCategoricalScore)),
    },
  ];
};

/**
 * Build the Ratings & style fit section.
 * @remarks
 * GPR LEADS the section as its own meter, which is the design's shape: the
 * hero pill states the number and this bar places it on the 0-100 scale. It
 * repeats the Key info `gpr` row on purpose — that row is the field list's
 * entry and this one is the rating summary — so neither surface may be dropped
 * in favour of the other without a product call.
 *
 * The two archetype rows carry no meter: they are text labels, not scores, and
 * a bar under a label like "Deep-lying playmaker" would be measuring nothing.
 * @param player - The player.
 * @returns The ratings rows, in render order.
 */
export const buildRatingsRows = (
  player: PreviewablePlayer
): readonly PlayerPreviewInfoItem[] => {
  const bio = player.bioData;

  return [
    {
      key: "gpr-rating",
      label: "GPR",
      // `bioData.gpr` — `Player.gpr` is deprecated and null off searchPlayers.
      value: orEmpty(formatScore(bio?.gpr)),
      percent: toPercent(bio?.gpr, RATING_SCORE_MAX),
      barTone: "success",
    },
    {
      key: "role-archetype",
      label: "Role archetype",
      value: orEmpty(bio?.roleArchetype),
    },
    {
      key: "secondary-role-archetype",
      label: "Secondary archetype",
      value: orEmpty(bio?.secondaryRoleArchetype),
    },
    /* Scaled to a percentage before BOTH the value and the bar — the backend
       sends 0-1. Untouched, a 0.8 fit rendered as the literal string "0.8"
       beside a bar 0.8% wide. */
    {
      key: "fit-score",
      label: "Player fit",
      value: orEmpty(formatScore(toFitPercentValue(bio?.fitScore))),
      percent: toPercent(toFitPercentValue(bio?.fitScore), RATING_SCORE_MAX),
    },
    {
      key: "team-style-fit",
      label: "Team style fit",
      value: orEmpty(formatScore(bio?.teamStyleFit)),
      percent: toPercent(bio?.teamStyleFit, RATING_SCORE_MAX),
    },
    /* NO BAR, for the same reason the Attributes rows lost theirs.
       `getPlayerCompositeMetrics` files Physical Score inside
       `getPlayerCategoricalMetrics` on the SAME 40/85 colour window as
       Carrying/Dribbling/Passing/Shooting — it is a member of the unbounded
       categorical family, not of the GPR family, so the 0-100 denominator it
       was borrowing was never its own.

       FLAGGED, not proven: I did not observe live `physicalScore` values, so I
       cannot say whether they overflow 100 the way the attributes do. Treating
       the family consistently is the safe call either way — a missing bar
       understates nothing, where a wrong denominator asserts a falsehood. If it
       turns out to be genuinely 0-100, restoring the meter is one line. */
    {
      key: "physical-score",
      label: "Physical",
      value: orEmpty(formatScore(bio?.physicalScore)),
    },
  ];
};

/**
 * Count the distinct matches the org's scouts have observed for this player.
 * @remarks
 * This is the honest reading of "matches watched": the schema exposes NO
 * `matchesWatched` field, so the count is derived from the distinct matches
 * attached to the player's scout reports. `ggMatchKeys` is the provider-agnostic
 * identifier and is preferred; a report that predates it falls back to its
 * `matches[]` entries keyed by date+opponent.
 * @param player - The player.
 * @returns The distinct observed-match count.
 */
export const countMatchesWatched = (player: PreviewablePlayer): number => {
  const keys = (player.scoutReports ?? []).flatMap(report => {
    if (!report) return [];

    const matchKeys = report.ggMatchKeys ?? [];
    if (matchKeys.length) return matchKeys.map(key => `gg:${key}`);

    return (report.matches ?? [])
      .filter(match => match != null)
      .map(match => `m:${match.matchDate ?? ""}|${match.opponent ?? ""}`);
  });

  return new Set(keys).size;
};

/**
 * One `listBasicCompetitionStats` row — the Transfermarkt-derived
 * per-player/league/season materialised view.
 * @remarks
 * Structural rather than the generated fragment so tests can build a row
 * without casting.
 */
export interface BasicCompetitionStatRow {
  readonly seasonId: string;
  readonly basicAppearances?: number | null;
  readonly basicGoals?: number | null;
  readonly basicAssists?: number | null;
  readonly basicMinutes?: number | null;
}

/** Aggregated single-season totals. */
export interface SeasonTotals {
  readonly appearances: number;
  readonly goals: number;
  readonly assists: number;
  readonly minutes: number;
}

/**
 * Pick the player's most recent season id from the basic-stats rows.
 * @remarks
 * `BasicCompetitionStats.seasonId` is an opaque UUID — it carries no ordering —
 * so recency comes from `seasonIdToYear`, the seasonId → `ggUnifiedSeasonKey`
 * mapping the player-detail feature already derives (and which encodes the
 * SE-4239 goalkeeper fallback). When no mapping is available the rows cannot be
 * ordered at all, and the season with the most appearances is used instead of
 * guessing — ties broken on the id string so the result is deterministic
 * regardless of backend row order.
 * @param rows - The player's basic competition stats.
 * @param seasonIdToYear - seasonId → season-start-year lookup.
 * @returns The chosen season id, or `undefined` when there are no rows.
 */
export const selectLatestSeasonId = (
  rows: readonly BasicCompetitionStatRow[],
  seasonIdToYear: Readonly<Record<string, number>> = {}
): string | undefined => {
  if (!rows.length) return undefined;

  const seasonIds = [...new Set(rows.map(row => row.seasonId))];

  const [firstMapped, ...restMapped] = seasonIds.filter(
    id => seasonIdToYear[id] != null
  );
  if (firstMapped !== undefined) {
    return restMapped.reduce((latest, id) => {
      const year = seasonIdToYear[id] as number;
      const latestYear = seasonIdToYear[latest] as number;
      if (year !== latestYear) return year > latestYear ? id : latest;
      return id < latest ? id : latest;
    }, firstMapped);
  }

  // No year mapping — fall back to the most-played season rather than guessing.
  const [firstSeason, ...restSeasons] = seasonIds.map(id => ({
    id,
    appearances: rows
      .filter(row => row.seasonId === id)
      .reduce((sum, row) => sum + Math.round(row.basicAppearances ?? 0), 0),
  }));
  if (firstSeason === undefined) return undefined;

  return restSeasons.reduce((best, candidate) => {
    if (candidate.appearances !== best.appearances) {
      return candidate.appearances > best.appearances ? candidate : best;
    }
    return candidate.id < best.id ? candidate : best;
  }, firstSeason).id;
};

/**
 * Sum every competition row for one season into a single set of totals.
 * @remarks
 * A player's season spans several competitions (league, domestic cup, Europe),
 * each its own row — so the season figure is the SUM across rows sharing the
 * season id, not any single row.
 * @param rows - The player's basic competition stats.
 * @param seasonId - The season to aggregate; `undefined` yields zeroes.
 * @returns The aggregated totals.
 */
export const aggregateSeasonTotals = (
  rows: readonly BasicCompetitionStatRow[],
  seasonId: string | undefined
): SeasonTotals =>
  rows
    .filter(row => seasonId !== undefined && row.seasonId === seasonId)
    .reduce<SeasonTotals>(
      (totals, row) => ({
        appearances: totals.appearances + Math.round(row.basicAppearances ?? 0),
        goals: totals.goals + Math.round(row.basicGoals ?? 0),
        assists: totals.assists + Math.round(row.basicAssists ?? 0),
        minutes: totals.minutes + Math.round(row.basicMinutes ?? 0),
      }),
      { appearances: 0, goals: 0, assists: 0, minutes: 0 }
    );

/**
 * Build the Season form section.
 * @remarks
 * Minutes / goals / assists come from `listBasicCompetitionStats`
 * (`PlayerDetailListBasicCompetitionStats`) — the ONLY per-player source for
 * them in this schema. They are deliberately NOT taken from
 * `SeasonProviderMetrics`, which carries `minutesTotal` but only
 * `headerGoalsTotal` / `penaltyGoalsTotal` (components, not a goal total) and
 * was dropped from `PlayerDetailGetPlayer` by SE-5265 to stay under the SE-5063
 * complexity cap.
 *
 * `Matches watched` has NO schema field — it is derived from the distinct
 * matches attached to the player's scout reports (see
 * {@link countMatchesWatched}), which is the honest reading of "matches our
 * scouts watched" rather than an unrelated number relabelled.
 * @param player - The player.
 * @param daysInStage - Whole days on the current stage (SE-5497), or `null`.
 * @param basicStats - `listBasicCompetitionStats` rows for this player.
 * @param seasonIdToYear - seasonId → season-start-year lookup.
 * @param locale - The active UI locale, for the minutes group separator. Production
 *   callers pass this explicitly; the default exists only for tests.
 * @returns The season-form rows, in render order.
 */
export const buildSeasonFormRows = (
  player: PreviewablePlayer,
  daysInStage: number | null | undefined,
  basicStats: readonly BasicCompetitionStatRow[] = [],
  seasonIdToYear: Readonly<Record<string, number>> = {},
  locale: string = DEFAULT_UI_LOCALE
): readonly PlayerPreviewInfoItem[] => {
  const totals = aggregateSeasonTotals(
    basicStats,
    selectLatestSeasonId(basicStats, seasonIdToYear)
  );
  const hasSeason = basicStats.length > 0;

  return [
    {
      key: "minutes",
      label: "Minutes",
      // SE-7966: minutes are thousands-separated, so the separator follows
      // the account language. Was hardcoded "en-US".
      value: hasSeason ? formatNumber(totals.minutes, locale) : EMPTY_VALUE,
    },
    {
      key: "goals-assists",
      label: "Goals / assists",
      value: hasSeason ? `${totals.goals} / ${totals.assists}` : EMPTY_VALUE,
    },
    {
      key: "appearances",
      label: "Appearances",
      value: hasSeason ? String(totals.appearances) : EMPTY_VALUE,
    },
    {
      key: "matches-watched",
      label: "Matches watched",
      value: String(countMatchesWatched(player)),
    },
    {
      key: "days-in-stage",
      label: "Days in stage",
      value: daysInStage == null ? EMPTY_VALUE : String(daysInStage),
    },
  ];
};

/**
 * Build the scout-report list, newest first.
 * @param player - The player.
 * @returns The report rows.
 */
export const buildReportRows = (
  player: PreviewablePlayer
): readonly PlayerPreviewReportItem[] =>
  (player.scoutReports ?? [])
    .map((report, index) => {
      if (!report) return null;
      return {
        key: report.reportId ?? `report-${index}`,
        scoutName: orEmpty(report.scoutName),
        reportDate: orEmpty(formatPreviewDate(report.reportDate)),
        overallScore: formatScore(report.overallScore),
        sortKey: report.reportDate ? Date.parse(report.reportDate) : Number.NaN,
      };
    })
    .filter((row): row is NonNullable<typeof row> => row !== null)
    .sort((left, right) => {
      const leftKey = Number.isNaN(left.sortKey)
        ? Number.NEGATIVE_INFINITY
        : left.sortKey;
      const rightKey = Number.isNaN(right.sortKey)
        ? Number.NEGATIVE_INFINITY
        : right.sortKey;
      return rightKey - leftKey;
    })
    .map(({ sortKey: _sortKey, ...row }) => row);

/**
 * The headline verdict shown above the report list.
 * @remarks
 * The schema has no dedicated "verdict" field on `PlayerScoutReport`, so the
 * verdict is the most recent report's `overallScore` — labelled as such in the
 * UI so it is never mistaken for a distinct, scout-authored verdict.
 * @param player - The player.
 * @returns The latest overall score, or `undefined` when there is none.
 */
export const selectLatestVerdict = (
  player: PreviewablePlayer
): string | undefined => buildReportRows(player)[0]?.overallScore;
